import React from "react";
import { inject, observer } from 'mobx-react';

import Volume from "./Volume";
import SeekBar from "./SeekBar";
import PopupMenu from "../PopupMenu";
import { showToast } from '../../Utils/toast';
import api from '../../Utils/api';

// Inline SVG components
const PlayIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const PauseIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
  </svg>
);

const NextIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 4l10 8-10 8V4zm12 0v16h-2V4h2z"/>
  </svg>
);

const PreviousIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 4v16l-10-8 10-8zM6 4h2v16H6V4z"/>
  </svg>
);

const ShuffleIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8"></polyline>
    <line x1="4" y1="20" x2="21" y2="3"></line>
    <polyline points="21 16 21 21 16 21"></polyline>
    <line x1="15" y1="15" x2="21" y2="21"></line>
    <line x1="4" y1="4" x2="9" y2="9"></line>
  </svg>
);

function Controls({
  isPlaying,
  onNext,
  onPlayPause,
  onPrevious,
  onShuffle,
  shuffleEnabled,
  track,
  defaultVolume,
  onVolumeChange,
  currentPosition,
  currentUser,
  onDelete,
  onSeek,
  currentPlaylistId,
  PlaylistStore,
  onScrollToCurrentTrack,
}) {
  return (
    <div className="w-full bg-gradient-to-b from-neutral-900 to-black text-white border-t border-neutral-800 shadow-[0_-4px_12px_rgba(0,0,0,0.5)] z-50">
      <div className="flex flex-col md:flex-row items-stretch w-full">
        {/* Track Details Section - Desktop: Left side, Mobile: Top */}
        <div className="flex-1 flex items-center md:flex-[0.6] lg:flex-[0.65] min-w-0 order-2 md:order-1">
          {track && (
            <div
              className="flex items-center w-full p-2 md:p-2 cursor-pointer hover:bg-neutral-900 transition-colors"
              onClick={onScrollToCurrentTrack}
            >
              {/* Album Art */}
              <div
                className="w-12 h-12 md:w-14 md:h-14 bg-cover bg-center flex-shrink-0 rounded border border-neutral-800"
                style={{ backgroundImage: `url(${track.albumArtUrl})` }}
              />

              {/* Track Info */}
              <div className="flex-1 flex flex-col min-w-0 px-2 md:px-3 gap-0.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm md:text-[13px] font-semibold text-white truncate flex-1 m-0 leading-tight">
                    {track.title}
                  </p>
                  {/* 3-dot menu */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <PopupMenu
                    trigger={
                      <button
                        className="p-1 hover:bg-neutral-800 rounded transition-colors flex-shrink-0 text-text-muted hover:text-text"
                        aria-label="Song options"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                    }
                    items={[
                      {
                        label: 'Add to playlist',
                        icon: (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        ),
                        onClick: () => {
                          PlaylistStore.openAddToPlaylistModal(
                            {
                              mediaId: track.mediaId,
                              title: track.title,
                              artist: track.artist
                            },
                            currentPlaylistId
                          );
                        },
                        variant: 'default',
                      },
                      // Show "Remove from playlist" only if viewing a specific non-virtual playlist
                      currentPlaylistId && !['public', 'my-songs'].includes(currentPlaylistId) && {
                        label: `Remove from playlist`,
                        icon: (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        ),
                        onClick: async () => {
                          try {
                            await showToast.promise(
                              api.removeSongFromPlaylist(currentPlaylistId, track.mediaId),
                              {
                                loading: 'Removing song...',
                                success: 'Song removed',
                                error: 'Failed to remove song',
                              }
                            );
                            // Refresh playlist
                            if (PlaylistStore.preview) {
                              await PlaylistStore.loadPlaylist(currentPlaylistId);
                            }
                          } catch (error) {
                            console.error('Failed to remove song:', error);
                          }
                        },
                        variant: 'danger',
                      },
                      // Delete or Filter action
                      {
                        label: currentUser.userId === track.userId ? 'Delete song' : 'Filter song',
                        icon: currentUser.userId === track.userId ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 423.01 292.33">
                            <path d="M708,607.16a266.84,266.84,0,0,1-70.47,13.2A249.78,249.78,0,0,1,426,494.42a263.61,263.61,0,0,1,80.48-83.67l38.84,37.86a103.14,103.14,0,0,0,140.77,137.2Z" transform="translate(-426.03 -340)"/>
                            <path d="M849,494.45v1a264.43,264.43,0,0,1-83.79,84.67q-2,1.26-4,2.46L725.85,548.1A103.13,103.13,0,0,0,582,407.92l-23.75-23.14a259.36,259.36,0,0,1,79.26-15.48A256.71,256.71,0,0,1,849,494.45Z" transform="translate(-426.03 -340)"/>
                            <path d="M699.81,494.83a61.93,61.93,0,0,1-4.57,23.44l-82.6-80.51a62.28,62.28,0,0,1,87.17,57.07Z" transform="translate(-426.03 -340)"/>
                            <path d="M654.31,554.81a62.3,62.3,0,0,1-77.15-75.2Z" transform="translate(-426.03 -340)"/>
                            <rect x="443.37" y="471.13" width="394.79" height="30.08" rx="5.76" transform="translate(95.22 -649.24) rotate(44.27)"/>
                          </svg>
                        ),
                        onClick: () => {
                          if (
                            window.confirm(
                              currentUser.userId === track.userId
                                ? "Are you sure you want to delete this song?"
                                : "Are you sure you want to filter this song?"
                            )
                          )
                            onDelete(track.mediaId);
                        },
                        variant: 'danger',
                      },
                    ].filter(Boolean)}
                    position="left-center"
                  />
                  </div>
                </div>
                <p className="text-[11px] text-text-muted m-0 truncate leading-tight">
                  {track.artist} <span className="text-text-dim">·</span> {track.username}
                </p>
                <SeekBar track={track} currentPosition={currentPosition} onSeek={onSeek} />
              </div>
            </div>
          )}
        </div>

        {/* Playback Controls Section - Desktop: Right side, Mobile: Bottom */}
        <div className="flex items-center justify-center md:flex-[0.4] lg:flex-[0.35] order-1 md:order-2 py-1.5 md:py-0">
          <div className="flex items-center justify-center gap-2 md:gap-3 px-3">
            {/* Shuffle Button */}
            <button
              onClick={onShuffle}
              className={`hover:opacity-60 transition-opacity p-1 ${
                shuffleEnabled
                  ? 'text-blue-400'
                  : 'text-text-muted'
              }`}
              aria-label={shuffleEnabled ? "Disable shuffle" : "Enable shuffle"}
              title={shuffleEnabled ? "Shuffle on" : "Shuffle off"}
            >
              <ShuffleIcon className="w-4 h-4" />
            </button>

            {/* Previous Button */}
            <button
              onClick={onPrevious}
              className="hover:opacity-60 transition-opacity p-1 text-text-muted hover:text-text"
              aria-label="Previous track"
            >
              <PreviousIcon className="w-7 h-7 md:w-7 md:h-7" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={onPlayPause}
              className="hover:opacity-90 transition-opacity text-text"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <PauseIcon className="w-10 h-10 md:w-11 md:h-11 bg-white text-black rounded-full p-2.5 shadow-md" />
              ) : (
                <PlayIcon className="w-10 h-10 md:w-11 md:h-11 bg-accent text-white rounded-full p-2.5 shadow-md" />
              )}
            </button>

            {/* Next Button */}
            <button
              onClick={onNext}
              className="hover:opacity-60 transition-opacity p-1 text-text-muted hover:text-text"
              aria-label="Next track"
            >
              <NextIcon className="w-7 h-7 md:w-7 md:h-7" />
            </button>

            {/* Volume Control - Hidden on mobile */}
            <div className="hidden md:block ml-2">
              <Volume onChange={onVolumeChange} defaultValue={defaultVolume} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default inject('PlaylistStore')(observer(Controls));
